import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountsByAssignmentScopeQueryKey,
  accountsByAssignmentScopeQueryOptions,
  accountsQueryOptions,
  organizationsQueryOptions,
  rolesQueryOptions,
  sitesQueryOptions,
  studiesQueryOptions,
  updateAccountRolesForScopeMutateOptions,
} from '../../queryOptions';
import type { UpdateAccountRolesForScopeVariables } from '../../queryOptions';
import RoleAssignmentsTable from './roleAssignmentsComponents/RoleAssignmentsTable';
import {
  Text,
  Tabs,
  VStack,
  Card,
  HStack,
  LoadingSpinner,
} from '@adc/parallax-component-library';
import SearchableDropdown, {
  SearchableDropdownItem,
} from '../searchableDropdown/SearchableDropdown';
import { EntityType } from 'adc-sirius-module-common';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../reducers/hooks';
import { changeCurrentOrganization } from '../../reducers/organization/reducer';
import { ROUTES } from '../../constants';
import { handleGetCapabilities } from '../../services/auth';
import i18n from '../../utilities/i18n';
import type {
  AccountRoleAssignmentScope,
  AccountWithRoleAssignments,
  ApiErrorResponse,
} from '../../models';

enum AssignmentScopeId {
  System = 'system',
  Organization = 'organization',
  Study = 'study',
  Site = 'site',
}

const getEntityTypeForScope = (scope: AssignmentScopeId): EntityType => {
  switch (scope) {
    case AssignmentScopeId.System:
      return EntityType.System;
    case AssignmentScopeId.Organization:
      return EntityType.Organization;
    case AssignmentScopeId.Study:
      return EntityType.Study;
    case AssignmentScopeId.Site:
      return EntityType.StudySite;
  }
};

type TabConfigItem = {
  scope: AssignmentScopeId;
  key: string;
  headerAction: React.ReactElement | undefined;
};

/**
 * View for displaying user accounts and roles organized by context
 * @returns {React.JSX.Element}
 */
export default function RoleAssignments() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const capabilities = useAppSelector((state) => state.capabilities);
  const capabilitiesLoaded = capabilities.isLoaded !== false;

  const [selectedScope, setSelectedScope] = useState<AssignmentScopeId | null>(
    null,
  );
  const isScopeInitialized = capabilitiesLoaded && selectedScope !== null;
  const activeScope = selectedScope ?? AssignmentScopeId.Organization;

  useEffect(() => {
    if (!capabilitiesLoaded || selectedScope !== null) {
      return;
    }

    setSelectedScope(
      capabilities.canAssignSystem
        ? AssignmentScopeId.System
        : AssignmentScopeId.Organization,
    );
  }, [capabilitiesLoaded, capabilities.canAssignSystem, selectedScope]);

  const [selectedOrganizationIndex, setSelectedOrganizationIndex] = useState(0);
  const [selectedStudyIndex, setSelectedStudyIndex] = useState(0);
  const [selectedSiteIndex, setSelectedSiteIndex] = useState(0);

  const { currentOrgKey: reduxOrgKey } = useAppSelector(
    (state) => state.organizations,
  );

  const roleAssignmentTabs = [
    ...(capabilities.canAssignSystem
      ? [
          {
            scope: AssignmentScopeId.System,
            tab: {
              'aria-label': i18n.t('Page_RoleAssignments.tabs.system'),
              label: i18n.t('Page_RoleAssignments.tabs.system'),
            },
          },
        ]
      : []),
    {
      scope: AssignmentScopeId.Organization,
      tab: {
        'aria-label': i18n.t('Page_RoleAssignments.tabs.organization'),
        label: i18n.t('Page_RoleAssignments.tabs.organization'),
      },
    },
    {
      scope: AssignmentScopeId.Study,
      tab: {
        'aria-label': i18n.t('Page_RoleAssignments.tabs.study'),
        label: i18n.t('Page_RoleAssignments.tabs.study'),
      },
    },
    {
      scope: AssignmentScopeId.Site,
      tab: {
        'aria-label': i18n.t('Page_RoleAssignments.tabs.site'),
        label: i18n.t('Page_RoleAssignments.tabs.site'),
      },
    },
  ];

  const tabs = roleAssignmentTabs.map((tabItem) => tabItem.tab);
  const selectedScopeTabIndex = roleAssignmentTabs.findIndex(
    (tabItem) => tabItem.scope === activeScope,
  );
  const selectedTabIndex =
    selectedScopeTabIndex === -1 ? 0 : selectedScopeTabIndex;

  const organizations = useQuery(organizationsQueryOptions());
  const studies = useQuery(
    studiesQueryOptions(
      {
        organizationKey:
          organizations.data?.[selectedOrganizationIndex]?.key ?? '',
      },
      {
        enabled:
          isScopeInitialized &&
          !!organizations.data?.[selectedOrganizationIndex]?.key &&
          (activeScope === AssignmentScopeId.Study ||
            activeScope === AssignmentScopeId.Site),
      },
    ),
  );
  const sites = useQuery(
    sitesQueryOptions(
      { studyKey: studies.data?.[selectedStudyIndex]?.key ?? '' },
      {
        enabled:
          isScopeInitialized &&
          !!studies.data?.[selectedStudyIndex]?.key &&
          activeScope === AssignmentScopeId.Site,
      },
    ),
  );

  const currentOrganization = organizations.data?.[selectedOrganizationIndex];
  const currentOrgKey = currentOrganization?.key ?? '';
  const currentStudy = studies.data?.[selectedStudyIndex];
  const currentStudyKey = currentStudy?.key ?? '';
  const currentSite = sites.data?.[selectedSiteIndex];
  const currentSiteKey = currentSite?.key ?? '';

  const activeAssignmentScope = useMemo<AccountRoleAssignmentScope>(() => {
    switch (activeScope) {
      case AssignmentScopeId.Organization:
        return {
          entityType: EntityType.Organization,
          entityKey: currentOrgKey,
        };
      case AssignmentScopeId.Study:
        return {
          entityType: EntityType.Study,
          entityKey: currentStudyKey,
        };
      case AssignmentScopeId.Site:
        return {
          entityType: EntityType.StudySite,
          entityKey: currentSiteKey,
        };
      default:
        return { entityType: EntityType.System };
    }
  }, [activeScope, currentOrgKey, currentSiteKey, currentStudyKey]);

  const accountsWithRoleAssignments = useQuery(
    accountsByAssignmentScopeQueryOptions(activeAssignmentScope),
  );

  const roles = useQuery(
    rolesQueryOptions({
      entityType: getEntityTypeForScope(activeScope),
    }),
  );

  const allAccounts = useQuery(accountsQueryOptions());

  const handleOrganizationChange = (item: SearchableDropdownItem) => {
    const index = organizations.data?.findIndex((org) => org.key === item.key);

    if (index !== undefined && index !== -1) {
      setSelectedOrganizationIndex(index);
    }
  };

  const handleStudyChange = (item: SearchableDropdownItem) => {
    const index = studies.data?.findIndex((study) => study.key === item.key);

    if (index !== undefined && index !== -1) {
      setSelectedStudyIndex(index);
    }
  };

  const handleSiteChange = (item: SearchableDropdownItem) => {
    const index = sites.data?.findIndex((site) => site.key === item.key);

    if (index !== undefined && index !== -1) {
      setSelectedSiteIndex(index);
    }
  };

  const updateRolesMutation = useMutation(
    updateAccountRolesForScopeMutateOptions({
      onSuccess: (
        _updatedAccounts: AccountWithRoleAssignments[],
        mutationVariables: UpdateAccountRolesForScopeVariables,
      ) => {
        // refresh the accounts with role assignments for the current scope after a successful update
        queryClient.invalidateQueries({
          queryKey: accountsByAssignmentScopeQueryKey(mutationVariables.scope),
        });

        // refresh the current user's capabilities in case their own roles have been updated
        void handleGetCapabilities().catch(() => undefined);
      },
    }),
  );

  const activeEntityScope = (() => {
    switch (selectedScope) {
      case AssignmentScopeId.Organization:
        return {
          entityKey: currentOrgKey,
          entityQuery: organizations,
        };
      case AssignmentScopeId.Study:
        return {
          entityKey: currentStudyKey,
          entityQuery: studies,
        };
      case AssignmentScopeId.Site:
        return {
          entityKey: currentSiteKey,
          entityQuery: sites,
        };
      default:
        return {};
    }
  })();

  const isLoadingAccounts =
    (activeAssignmentScope.entityType === EntityType.System ||
      !!activeAssignmentScope.entityKey) &&
    accountsWithRoleAssignments.isPending;

  const isDisabledDueToLoading =
    isLoadingAccounts ||
    roles.isPending ||
    allAccounts.isPending ||
    updateRolesMutation.isPending;

  const isLoadError = useMemo(() => {
    return (
      (accountsWithRoleAssignments.isError &&
        (accountsWithRoleAssignments.error as ApiErrorResponse)?.status !==
          403) ||
      (roles.isError && (roles.error as ApiErrorResponse)?.status !== 403) ||
      (!!activeEntityScope.entityQuery &&
        activeEntityScope.entityQuery.isError &&
        (activeEntityScope.entityQuery.error as ApiErrorResponse)?.status !==
          403)
    );
  }, [
    accountsWithRoleAssignments.isError,
    accountsWithRoleAssignments.error,
    roles.isError,
    roles.error,
    activeEntityScope.entityQuery,
  ]);

  const isPermissionsError = useMemo(() => {
    return (
      (accountsWithRoleAssignments.isError &&
        (accountsWithRoleAssignments.error as ApiErrorResponse)?.status ===
          403) ||
      (roles.isError && (roles.error as ApiErrorResponse)?.status === 403) ||
      (!!activeEntityScope.entityQuery &&
        activeEntityScope.entityQuery.isError &&
        (activeEntityScope.entityQuery.error as ApiErrorResponse)?.status ===
          403)
    );
  }, [
    accountsWithRoleAssignments.isError,
    accountsWithRoleAssignments.error,
    roles.isError,
    roles.error,
    activeEntityScope.entityQuery,
  ]);

  // if the user's capabilities have changed due to altering their own roles
  // and they no longer have access to this feature, redirect them to the root page
  useEffect(() => {
    if (!capabilities.isLoaded || capabilities.canGetAssignments) {
      return;
    }

    // reset the current organization to avoid showing stale data on the dashboard
    dispatch(changeCurrentOrganization(''));
    queryClient.invalidateQueries({
      queryKey: organizationsQueryOptions().queryKey,
    });

    navigate(ROUTES.ROOT, { replace: true });
  }, [
    capabilities.isLoaded,
    capabilities.canGetAssignments,
    dispatch,
    navigate,
    queryClient,
  ]);

  useEffect(() => {
    if (
      !capabilities.canAssignSystem &&
      selectedScope === AssignmentScopeId.System
    ) {
      setSelectedScope(AssignmentScopeId.Organization);
    }
  }, [capabilities.canAssignSystem, selectedScope]);

  useEffect(() => {
    if (organizations.data && reduxOrgKey) {
      const index = organizations.data.findIndex(
        (org) => org.key === reduxOrgKey,
      );

      if (index !== -1) {
        setSelectedOrganizationIndex(index);
      }
    }
  }, [organizations.data, reduxOrgKey]);

  useEffect(() => {
    setSelectedStudyIndex(0);
    setSelectedSiteIndex(0);
  }, [selectedOrganizationIndex]);

  const onSaveRoleChanges = async (
    account: AccountWithRoleAssignments,
    addRoleKeys: string[],
    removeRoleKeys: string[],
  ): Promise<void> => {
    if (
      activeAssignmentScope.entityType !== EntityType.System &&
      !activeAssignmentScope.entityKey
    ) {
      return;
    }

    await updateRolesMutation.mutateAsync({
      scope: activeAssignmentScope,
      updateRequest: {
        userKey: account.key,
        addRoleKeys,
        removeRoleKeys,
      },
    });
  };

  const renderOrganizationHeaderAction = () => {
    if (organizations.data?.length === 0) {
      return (
        <HStack flexGrow={1} alignSelf="center">
          <Text type="$bodySmall.default">
            {i18n.t('Page_RoleAssignments.noOrganizations')}
          </Text>
        </HStack>
      );
    }

    return (
      <VStack flexGrow={1}>
        <SearchableDropdown
          items={
            organizations.data?.map((org) => ({
              key: org.key,
              value: org.name,
            })) ?? []
          }
          selectedItem={
            currentOrganization
              ? {
                  key: currentOrganization.key,
                  value: currentOrganization.name,
                }
              : undefined
          }
          onChange={handleOrganizationChange}
          size="medium"
          disabled={organizations.isPending || isDisabledDueToLoading}
        />
      </VStack>
    );
  };

  const renderStudyHeaderAction = () => {
    if (studies.data?.length === 0) {
      return (
        <HStack flexGrow={1} alignSelf="center">
          <Text type="$bodySmall.default">
            {i18n.t('Page_RoleAssignments.noStudies')}
          </Text>
        </HStack>
      );
    }

    return (
      <VStack flexGrow={1}>
        <SearchableDropdown
          items={
            studies.data?.map((study) => ({
              key: study.key,
              value: study.name,
            })) ?? []
          }
          selectedItem={
            currentStudy
              ? { key: currentStudy.key, value: currentStudy.name }
              : undefined
          }
          onChange={handleStudyChange}
          size="medium"
          disabled={studies.isPending || isDisabledDueToLoading}
        />
      </VStack>
    );
  };

  const renderSiteHeaderAction = () => {
    if (!currentStudyKey || sites.data?.length === 0) {
      return (
        <HStack flexGrow={1} alignSelf="center">
          <Text type="$bodySmall.default">
            {i18n.t('Page_RoleAssignments.noSites')}
          </Text>
        </HStack>
      );
    }

    return (
      <VStack flexGrow={1}>
        <SearchableDropdown
          items={
            sites.data?.map((site) => ({
              key: site.key,
              value: site.name,
            })) ?? []
          }
          selectedItem={
            currentSite
              ? { key: currentSite.key, value: currentSite.name }
              : undefined
          }
          onChange={handleSiteChange}
          size="medium"
          disabled={sites.isPending || isDisabledDueToLoading}
        />
      </VStack>
    );
  };

  const activeTab: TabConfigItem = (() => {
    switch (activeScope) {
      case AssignmentScopeId.Organization:
        return {
          scope: activeScope,
          key: currentOrgKey,
          headerAction: renderOrganizationHeaderAction(),
        };
      case AssignmentScopeId.Study:
        return {
          scope: activeScope,
          key: currentStudyKey,
          headerAction: renderStudyHeaderAction(),
        };
      case AssignmentScopeId.Site:
        return {
          scope: activeScope,
          key: currentSiteKey,
          headerAction: renderSiteHeaderAction(),
        };
      default:
        return {
          scope: activeScope,
          key: '',
          headerAction: undefined,
        };
    }
  })();

  const renderEntityHierarchy = () => {
    if (
      selectedScope !== AssignmentScopeId.System &&
      !organizations.data?.length
    ) {
      return null;
    }

    return (
      <HStack gap="$2" alignItems="center" testID="entity-hierarchy">
        {capabilities?.canAssignSystem ? (
          <Text type="$labelBase.default" color="$text.80">
            {i18n.t('Page_RoleAssignments.tabs.system')}
          </Text>
        ) : null}
        {selectedScope !== AssignmentScopeId.System &&
        organizations.data?.length ? (
          <>
            {capabilities?.canAssignSystem ? (
              <Text type="$labelBase.default" color="$text.80">
                {'>'}
              </Text>
            ) : null}
            <Text type="$labelBase.default" color="$text.80">
              {organizations.data?.[selectedOrganizationIndex]?.name}
            </Text>
          </>
        ) : null}
        {(selectedScope === AssignmentScopeId.Study ||
          selectedScope === AssignmentScopeId.Site) &&
        studies.data?.length ? (
          <>
            <Text type="$labelBase.default" color="$text.80">
              {'>'}
            </Text>
            <Text type="$labelBase.default" color="$text.80">
              {studies.data?.[selectedStudyIndex]?.name}
            </Text>
          </>
        ) : null}
        {selectedScope === AssignmentScopeId.Site && sites.data?.length ? (
          <>
            <Text type="$labelBase.default" color="$text.80">
              {'>'}
            </Text>
            <Text type="$labelBase.default" color="$text.80">
              {sites.data?.[selectedSiteIndex]?.name}
            </Text>
          </>
        ) : null}
      </HStack>
    );
  };

  return !isScopeInitialized ? (
    <VStack alignItems="center" justifyContent="center" flexGrow={1}>
      <LoadingSpinner testID="capabilities-loading" />
    </VStack>
  ) : (
    <Card
      title={i18n.t('Page_RoleAssignments.title')}
      titleAriaLevel={1}
      titleTypeToken="$h4.strong"
      testID="role-assignments-card"
    >
      <VStack gap="$3">
        {renderEntityHierarchy()}
        <Tabs
          tabs={tabs}
          selectedIndex={selectedTabIndex}
          onSelect={(index) => {
            const selectedTab = roleAssignmentTabs[index];

            if (selectedTab) {
              setSelectedScope(selectedTab.scope);
            }
          }}
          testID="account-roles-tabs"
        />
        {/* Update key when tab or dropdown changes to force table remount, resetting filters and selections intentionally */}
        <RoleAssignmentsTable
          key={`${activeTab.scope}-${activeTab.key}`}
          allAccounts={allAccounts.data || []}
          accountsWithRoleAssignments={accountsWithRoleAssignments.data || []}
          roles={roles.data || []}
          isLoading={
            isLoadingAccounts || roles.isPending || allAccounts.isPending
          }
          isLoadError={isLoadError}
          isPermissionsError={isPermissionsError}
          onSaveRoleChanges={onSaveRoleChanges}
          isSavingRoleChanges={updateRolesMutation.isPending}
          headerAction={activeTab.headerAction}
          activeEntityType={getEntityTypeForScope(activeScope)}
          noEntityData={
            !!activeEntityScope.entityQuery &&
            !activeEntityScope.entityQuery?.data?.length
          }
        />
      </VStack>
    </Card>
  );
}
